import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from 'react-bootstrap';
import { useLanguage } from '../contexts/LanguageContext';

// Import Data
import milestonesData from '../data/milestones.json';
import ptJson from '../translations/pt.json';
import enJson from '../translations/en.json';

// --- GAME CONFIG ---
const PRESENT_YEAR = 2025;
const PIXELS_PER_YEAR = 2600;
const GROUND_MARGIN = 120;
const PLANE_HUD_X = 250;
const INITIAL_CRUISE_SPEED = 110; // Slightly slower for better control

export default function TrajectoryGame({ onClose }) {
    const { language } = useLanguage();

    const t = useMemo(() => {
        const translations = language === 'en' ? enJson : ptJson;
        return translations.game;
    }, [language]);

    const startYear = useMemo(() => {
        if (!milestonesData || milestonesData.length === 0) return 2010;
        const years = milestonesData.map(m => m.start);
        return Math.min(...years) - 3;
    }, []);

    const milestones = useMemo(() => {
        if (!milestonesData) return [];
        const data = Array.isArray(milestonesData) ? milestonesData : [];
        return data.map(m => ({
            ...m,
            title: m[language]?.title || m.pt.title,
            inst: m[language]?.inst || m.pt.inst,
            role: m[language]?.role || m.pt.role,
            desc: m[language]?.desc || m.pt.desc
        }));
    }, [language]);

    const canvasRef = useRef(null);
    const skyBgRef = useRef(null);
    const fireworkParticles = useRef([]);

    const [gameState, setGameState] = useState('START');
    const [activeMilestone, setActiveMilestone] = useState(null);
    const [isMuted, setIsMuted] = useState(false);
    const [progress, setProgress] = useState(0);
    const [showVictoryUI, setShowVictoryUI] = useState(false);
    const [showInstructions, setShowInstructions] = useState(false);

    const audioContext = useRef(null);
    const nodes = useRef({ mainGain: null, engineGain: null, engine: null });

    const engine = useRef({
        posX: 0, posY: 0, targetY: 0,
        velX: 0, velY: 0, pitch: 0,
        width: 0, height: 0,
        clouds: [],
        lastTime: 0,
        vsi: 0,
        prevVelX: 0,
        prevAlt: 0
    });

    // CRITICAL: Robust Scroll Control
    useEffect(() => {
        const originalStyle = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleGlobalWheel = (e) => {
            e.preventDefault();
            if (['TAKEOFF', 'PLAYING'].includes(engine.current.gameStateForRef)) {
                const groundY = engine.current.height - GROUND_MARGIN;
                const skyHeight = groundY - 80;
                const altScale = 30000 / skyHeight;

                engine.current.targetY += e.deltaY * 1.3;

                // Dynamic limits based on flight state
                const isCruise = engine.current.gameStateForRef === 'PLAYING';
                const minY = 80; // 30,000 ft
                const floorAlt = isCruise ? 3000 : 10;
                const maxY = groundY - (floorAlt / altScale);

                if (engine.current.targetY > maxY) engine.current.targetY = maxY;
                if (engine.current.targetY < minY) engine.current.targetY = minY;
            }
        };

        let touchStartY = 0;
        const handleGlobalTouchStart = (e) => {
            touchStartY = e.touches[0].clientY;
        };

        const handleGlobalTouchMove = (e) => {
            e.preventDefault();
            if (['TAKEOFF', 'PLAYING'].includes(engine.current.gameStateForRef)) {
                const currentY = e.touches[0].clientY;
                const deltaY = (touchStartY - currentY) * 3.2; // Increased sensitivity for better mobile control
                touchStartY = currentY;

                const groundY = engine.current.height - GROUND_MARGIN;
                const skyHeight = groundY - 80;
                const altScale = 30000 / skyHeight;

                engine.current.targetY -= deltaY; // Invert for natural drag feel

                // Dynamic limits based on flight state
                const isCruise = engine.current.gameStateForRef === 'PLAYING';
                const minY = 80;
                const floorAlt = isCruise ? 3000 : 10;
                const maxY = groundY - (floorAlt / altScale);

                if (engine.current.targetY > maxY) engine.current.targetY = maxY;
                if (engine.current.targetY < minY) engine.current.targetY = minY;
            }
        };

        const blockTouch = (e) => { if (e.target.tagName !== 'BUTTON') e.preventDefault(); };

        window.addEventListener('wheel', handleGlobalWheel, { passive: false });
        window.addEventListener('touchstart', handleGlobalTouchStart, { passive: false });
        window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });

        return () => {
            document.body.style.overflow = originalStyle;
            window.removeEventListener('wheel', handleGlobalWheel);
            window.removeEventListener('touchstart', handleGlobalTouchStart);
            window.removeEventListener('touchmove', handleGlobalTouchMove);
            if (audioContext.current && audioContext.current.state !== 'closed') {
                audioContext.current.close();
            }
        };
    }, []);

    // Sync gameState to ref for the event listener (to avoid staleness)
    useEffect(() => {
        engine.current.gameStateForRef = gameState;
    }, [gameState]);

    useEffect(() => {
        const img = new Image();
        img.src = '/sky_bg.png';
        img.onload = () => { skyBgRef.current = img; };
    }, []);

    const initAudio = () => {
        if (audioContext.current) return;
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioContext.current = ctx;

        const mainGain = ctx.createGain();
        mainGain.gain.setTargetAtTime(isMuted ? 0 : 0.4, ctx.currentTime, 0.1);
        mainGain.connect(ctx.destination);
        nodes.current.mainGain = mainGain;

        const engineGain = ctx.createGain();
        engineGain.gain.value = 0;
        engineGain.connect(mainGain);
        nodes.current.engineGain = engineGain;

        const osc = ctx.createOscillator();
        osc.type = 'square';
        osc.frequency.setValueAtTime(50, ctx.currentTime);
        osc.connect(engineGain);
        osc.start();
        nodes.current.engine = osc;
    };

    const playBeep = (freq = 440, duration = 0.1, type = 'triangle') => {
        if (!audioContext.current || isMuted) return;
        const osc = audioContext.current.createOscillator();
        const g = audioContext.current.createGain();
        osc.type = type;
        osc.frequency.setValueAtTime(freq, audioContext.current.currentTime);
        g.gain.setValueAtTime(0.2, audioContext.current.currentTime);
        g.gain.exponentialRampToValueAtTime(0.01, audioContext.current.currentTime + duration);
        osc.connect(g);
        g.connect(nodes.current.mainGain);
        osc.start();
        osc.stop(audioContext.current.currentTime + duration);
    };

    const playVictoryTune = () => {
        if (!audioContext.current || isMuted) return;
        const notes = [523.25, 659.25, 783.99, 1046.50];
        notes.forEach((note, i) => {
            setTimeout(() => playBeep(note, 0.4, 'square'), i * 150);
        });
    };

    useEffect(() => {
        if (nodes.current.mainGain) {
            nodes.current.mainGain.gain.setTargetAtTime(isMuted ? 0 : 0.4, audioContext.current.currentTime, 0.1);
        }
    }, [isMuted]);

    useEffect(() => {
        if (!nodes.current.engineGain) return;
        const flying = ['TAKEOFF', 'PLAYING', 'FINAL_APPROACH'].includes(gameState);
        nodes.current.engineGain.gain.setTargetAtTime(flying ? 0.05 : 0, audioContext.current.currentTime, 0.1);

        if (gameState === 'WIN') {
            playVictoryTune();
            setShowVictoryUI(false);
            setTimeout(() => setShowVictoryUI(true), 4000);
        }
    }, [gameState]);

    useEffect(() => {
        // Sort milestones by start year to ensure chronological order
        const sortedMilestones = [...milestones].sort((a, b) => a.start - b.start);

        const MIN_CLOUD_SPACING = 800; // Minimum horizontal distance between clouds
        const CLOUD_WIDTH = 450;
        const MIN_VERTICAL_SPACING = 120; // Minimum vertical distance for overlapping x positions

        // Track used positions to prevent overlaps
        const usedPositions = [];

        engine.current.clouds = sortedMilestones.map((m, i) => {
            // Calculate base position from timeline
            let baseX = (m.start - startYear) * PIXELS_PER_YEAR;

            // Check for horizontal overlaps and adjust if needed
            let finalX = baseX;
            let attempts = 0;
            const maxAttempts = 10;

            while (attempts < maxAttempts) {
                let hasOverlap = false;

                for (const pos of usedPositions) {
                    const horizontalDistance = Math.abs(finalX - pos.x);

                    // If clouds are too close horizontally
                    if (horizontalDistance < MIN_CLOUD_SPACING) {
                        hasOverlap = true;
                        // Push forward to maintain chronological flow
                        finalX = pos.x + MIN_CLOUD_SPACING;
                        break;
                    }
                }

                if (!hasOverlap) break;
                attempts++;
            }

            // Determine vertical position with intelligent variation
            let yPosition;

            // Create rhythm: alternate between high, mid, and low positions
            const heightPattern = i % 3;
            if (heightPattern === 0) {
                yPosition = 150 + Math.random() * 80; // High
            } else if (heightPattern === 1) {
                yPosition = 250 + Math.random() * 80; // Mid
            } else {
                yPosition = 350 + Math.random() * 80; // Low
            }

            // Check if there's a cloud very close horizontally
            // If so, ensure vertical separation
            for (const pos of usedPositions) {
                const horizontalDistance = Math.abs(finalX - pos.x);

                if (horizontalDistance < MIN_CLOUD_SPACING * 1.5) {
                    const verticalDistance = Math.abs(yPosition - pos.y);

                    // If too close vertically, shift to different height
                    if (verticalDistance < MIN_VERTICAL_SPACING) {
                        if (yPosition < pos.y) {
                            yPosition = Math.max(150, pos.y - MIN_VERTICAL_SPACING);
                        } else {
                            yPosition = Math.min(430, pos.y + MIN_VERTICAL_SPACING);
                        }
                    }
                }
            }

            // Record this position
            const cloudData = {
                ...m,
                id: i,
                x: finalX,
                w: CLOUD_WIDTH,
                y: yPosition,
                passed: false,
                emojis: Array.from({ length: 6 }).map(() => ({
                    dx: (Math.random() - 0.5) * 350,
                    dy: (Math.random() - 0.5) * 60,
                    s: 60 + Math.random() * 40
                }))
            };

            usedPositions.push({ x: finalX, y: yPosition });

            return cloudData;
        });
    }, [milestones, startYear]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let reqId;

        const createFirework = (x, y) => {
            const colors = ['#00f5ff', '#20ff70', '#ff00ff', '#ffff00', '#ffffff'];
            const color = colors[Math.floor(Math.random() * colors.length)];
            for (let i = 0; i < 40; i++) {
                const angle = Math.random() * Math.PI * 2;
                const speed = 2 + Math.random() * 5;
                fireworkParticles.current.push({
                    x, y,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed,
                    alpha: 1,
                    color,
                    size: 2 + Math.random() * 3
                });
            }
        };

        const loop = (time) => {
            let dt = (time - engine.current.lastTime) / 16.67 || 1;
            if (dt > 3) dt = 3; // Cap DT for stability
            engine.current.lastTime = time;

            if (canvas.parentElement) {
                if (canvas.width !== canvas.parentElement.clientWidth || canvas.height !== canvas.parentElement.clientHeight) {
                    canvas.width = canvas.parentElement.clientWidth;
                    canvas.height = canvas.parentElement.clientHeight;
                    engine.current.width = canvas.width;
                    engine.current.height = canvas.height;
                    if (engine.current.posY === 0) engine.current.posY = canvas.height - GROUND_MARGIN;
                    engine.current.targetY = engine.current.posY;
                }
            }
            update(dt);
            draw(ctx);

            if (gameState === 'WIN' && Math.random() < 0.12) {
                createFirework(Math.random() * canvas.width, Math.random() * canvas.height * 0.7);
            }

            reqId = requestAnimationFrame(loop);
        };

        const update = (dt) => {
            const e = engine.current;
            const groundY = e.height - GROUND_MARGIN;
            if (gameState === 'START') { e.posX = 0; e.velX = 0; e.posY = groundY; e.targetY = groundY; e.pitch = 0; setProgress(0); return; }

            if (gameState === 'TAKEOFF' || gameState === 'PLAYING' || gameState === 'FINAL_APPROACH') {
                const landingBuffer = 5000;
                const totalDist = (PRESENT_YEAR - startYear) * PIXELS_PER_YEAR + landingBuffer;

                if (e.velX < INITIAL_CRUISE_SPEED) {
                    e.velX += 2 * dt;
                } else if (gameState === 'PLAYING') {
                    const pathProgress = e.posX / ((PRESENT_YEAR - startYear) * PIXELS_PER_YEAR);
                    e.velX = INITIAL_CRUISE_SPEED + (pathProgress * 150);
                }

                setProgress(Math.min(100, (e.posX / (totalDist - landingBuffer)) * 100));

                if (nodes.current.engine) nodes.current.engine.frequency.setTargetAtTime(40 + (e.velX * 0.45), audioContext.current.currentTime, 0.1);

                if (gameState === 'TAKEOFF' && e.velX > 90) {
                    e.targetY = groundY - 300;
                    if (e.posY < groundY - 150) setGameState('PLAYING');
                }

                let responseActive = 0.22; // Snappy responsiveness

                if (gameState === 'FINAL_APPROACH') {
                    e.targetY = groundY;
                    responseActive = 0.03;
                    e.velX *= (1 - 0.018 * dt);
                    if (e.velX < 40) e.velX = 40;

                    if (e.posX >= totalDist - 100 && e.posY >= groundY - 0.1) {
                        setGameState('WIN');
                        e.velX = 0; e.velY = 0; e.posY = groundY; e.pitch = 0; e.vsi = 0;
                    }
                }

                const dy = e.targetY - e.posY;
                const dYClamped = Math.max(-250, Math.min(250, dy));
                e.velY = dYClamped * responseActive;

                if (e.velY > 15) e.velY = 15;
                if (e.velY < -15) e.velY = -15;

                const skyHeight = groundY - 80;
                const altScale = 30000 / skyHeight;
                e.vsi = -e.velY * 900;
                e.posY += e.velY * dt;
                e.pitch = Math.max(-0.35, Math.min(0.35, e.velY * 0.022));
                e.posX += (e.velX / 10) * dt;

                // Dynamic Floor Control
                const isCruise = gameState === 'PLAYING';
                const currentFloorAlt = isCruise ? 3000 : ((gameState === 'FINAL_APPROACH' || gameState === 'WIN') ? 0 : 10);
                const currentFloorY = groundY - (currentFloorAlt / altScale);

                if (e.posY > currentFloorY) {
                    e.posY = currentFloorY; e.pitch = 0; e.vsi = 0;
                    if (e.targetY > currentFloorY) e.targetY = currentFloorY;
                }
                if (e.posY < 80) { e.posY = 80; e.targetY = 80; }

                if (gameState === 'PLAYING') {
                    for (let cloud of e.clouds) {
                        const relX = cloud.x - e.posX + PLANE_HUD_X;
                        if (PLANE_HUD_X >= relX - 250 && PLANE_HUD_X <= relX + 250) {
                            if (Math.abs(e.posY - cloud.y) < 100 && !cloud.passed) {
                                setGameState('LANDED');
                                setActiveMilestone(cloud);
                                cloud.passed = true;
                                playBeep(660, 0.2);
                            }
                        }
                    }
                    if (e.posX > (PRESENT_YEAR - startYear) * PIXELS_PER_YEAR) setGameState('FINAL_APPROACH');
                }

                // Update Trends (Low-pass filtered for smooth display)
                e.prevVelX = e.prevVelX * 0.95 + e.velX * 0.05;
                e.prevAlt = e.prevAlt * 0.95 + (groundY - e.posY) * 0.05;
            }
            if (gameState === 'LANDED') { e.velX *= (1 - 0.22 * dt); if (e.velX < 1) e.velX = 0; }

            // Safe particle cleanup
            fireworkParticles.current = fireworkParticles.current.filter(p => {
                p.x += p.vx; p.y += p.vy; p.alpha -= 0.018 * dt;
                return p.alpha > 0;
            });
        };

        const draw = (ctx) => {
            const e = engine.current; const w = e.width; const h = e.height;
            if (w === 0 || h === 0) return;

            ctx.fillStyle = '#050a10'; ctx.fillRect(0, 0, w, h);
            if (skyBgRef.current) {
                const img = skyBgRef.current;
                const bgW = img.width * (h / img.height);
                const ox = (e.posX * 0.2) % (bgW * 2);
                if (ox < bgW) {
                    ctx.drawImage(img, -ox, 0, bgW + 1, h);
                    ctx.save(); ctx.translate(bgW - ox, 0); ctx.scale(-1, 1); ctx.drawImage(img, -bgW - 1, 0, bgW + 1, h); ctx.restore();
                } else {
                    const mox = ox - bgW; ctx.save(); ctx.translate(-mox, 0); ctx.scale(-1, 1); ctx.drawImage(img, -bgW - 1, 0, bgW + 1, h); ctx.restore();
                    ctx.drawImage(img, (2 * bgW) - ox, 0, bgW + 1, h);
                }
            }

            const gy = h - GROUND_MARGIN;
            ctx.fillStyle = 'rgba(10,10,15,0.85)'; ctx.fillRect(0, gy, w, GROUND_MARGIN);
            ctx.strokeStyle = '#00f5ff'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(w, gy); ctx.stroke();

            const winX = (PRESENT_YEAR - startYear) * PIXELS_PER_YEAR + 5000;
            const airportRelX = winX - e.posX + PLANE_HUD_X;
            if (airportRelX > -1200 && airportRelX < w + 1200) {
                ctx.save();
                ctx.font = '100px Arial'; ctx.textAlign = 'center';
                ctx.fillText('🌳🏢🌳🌳🏢🏢🏢     🌳🏢🏢🌳🌳🌳🏢🏢🌳🌳🌳🌳', airportRelX, gy - 20);
                ctx.strokeStyle = '#20ff70'; ctx.lineWidth = 10; ctx.setLineDash([30, 50]);
                ctx.beginPath(); ctx.moveTo(airportRelX - 3000, gy + 15); ctx.lineTo(airportRelX + 3000, gy + 15); ctx.stroke();
                ctx.setLineDash([]);
                ctx.restore();
            }

            e.clouds.forEach(c => {
                const cx = c.x - e.posX + PLANE_HUD_X;
                if (cx < -400 || cx > w + 400) return;
                ctx.save(); ctx.translate(cx, c.y);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
                c.emojis.forEach(emo => { ctx.beginPath(); ctx.arc(emo.dx, emo.dy, emo.s * 0.35, 0, Math.PI * 2); ctx.fill(); });
                ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
                c.emojis.forEach(emo => { ctx.font = `${emo.s}px Arial`; ctx.fillText('☁️', emo.dx, emo.dy); });
                ctx.fillStyle = '#1e3c72'; ctx.font = 'bold 10px "Inter"'; ctx.fillText(c.inst, 0, -40);
                ctx.font = '900 12px "Inter"'; ctx.fillText(c.title, 0, -25);
                ctx.font = '600 9px "Inter"'; ctx.fillText(`${t.ui.role} ${c.role}`, 0, -10);
                ctx.font = '500 9px "Inter"';
                const words = c.desc.split(' ');
                let line = '', lineCount = 0;
                for (let n = 0; n < words.length; n++) {
                    let testLine = line + words[n] + ' ';
                    if (ctx.measureText(testLine).width > 180) {
                        ctx.fillText(line, 0, 8 + (lineCount * 11)); line = words[n] + ' '; lineCount++;
                    } else { line = testLine; }
                }
                ctx.fillText(line, 0, 8 + (lineCount * 11)); ctx.font = 'bold 9px Courier'; ctx.fillText(`${c.start} → ${c.end}`, 0, 45);
                ctx.restore();
            });

            // SOLID AIRPLANE (No transparency)
            ctx.save();
            const planeX = w < 600 ? w * 0.2 : PLANE_HUD_X;
            ctx.translate(planeX, e.posY);
            ctx.rotate((30 * Math.PI / 180) + e.pitch * 3);

            // Agile & Clean airplane (No backing circle)
            const planeSize = w < 600 ? 50 : 65;
            ctx.font = `${planeSize}px Arial`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.shadowBlur = 18; ctx.shadowColor = 'rgba(0,0,0,0.9)';
            ctx.fillText('✈️', 0, 0);
            ctx.restore();

            fireworkParticles.current.forEach(p => {
                ctx.globalAlpha = p.alpha; ctx.fillStyle = p.color;
                ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
            });
            ctx.globalAlpha = 1;

            if (gameState !== 'WIN' || !showVictoryUI) drawUltimateCockpit(ctx, w, h, e);
        };

        const drawUltimateCockpit = (ctx, w, h, e) => {
            const isMobile = w < 768;
            const hudW = isMobile ? w * 0.95 : Math.min(850, w * 0.85);
            const maxHudH = GROUND_MARGIN - 10;
            const hudH = isMobile ? Math.min(100, maxHudH) : Math.min(115, maxHudH);
            const hx = w / 2 - hudW / 2;
            const groundY = h - GROUND_MARGIN;
            const hy = groundY + (GROUND_MARGIN - hudH) / 2;

            ctx.save();

            // Fundo do cockpit com textura de "metal escovado" simulada
            const bgGrad = ctx.createLinearGradient(hx, hy, hx, hy + hudH);
            bgGrad.addColorStop(0, '#0a1018');
            bgGrad.addColorStop(0.5, '#050a10');
            bgGrad.addColorStop(1, '#020508');
            ctx.fillStyle = bgGrad;
            ctx.strokeStyle = '#00f5ff';
            ctx.lineWidth = 2;
            ctx.shadowBlur = 15;
            ctx.shadowColor = 'rgba(0, 245, 255, 0.4)';
            ctx.beginPath();
            ctx.roundRect(hx, hy, hudW, hudH, isMobile ? 12 : 20);
            ctx.fill();
            ctx.stroke();
            ctx.shadowBlur = 0;

            // Desenhar "parafusos" decorativos
            const drawScrew = (sx, sy) => {
                ctx.fillStyle = '#1a2530';
                ctx.beginPath(); ctx.arc(sx, sy, 4, 0, Math.PI * 2); ctx.fill();
                ctx.strokeStyle = '#2a3540'; ctx.lineWidth = 1; ctx.stroke();
                ctx.beginPath(); ctx.moveTo(sx - 2, sy - 2); ctx.lineTo(sx + 2, sy + 2); ctx.stroke();
            };
            drawScrew(hx + 15, hy + 15); drawScrew(hx + hudW - 15, hy + 15);
            drawScrew(hx + 15, hy + hudH - 15); drawScrew(hx + hudW - 15, hy + hudH - 15);

            // Refinar Horizonte Artificial (Expandir no celular pois HDG/VSI sumiram)
            const aiW = isMobile ? hudW * 0.45 : 260;
            const aiH = hudH - (isMobile ? 25 : 30);
            const aiX = hx + hudW / 2 - aiW / 2;
            const aiY = hy + (isMobile ? 8 : 15);

            ctx.save();
            ctx.beginPath(); ctx.rect(aiX, aiY, aiW, aiH); ctx.clip();
            ctx.translate(aiX + aiW / 2, aiY + aiH / 2);
            ctx.rotate(e.pitch * 5);

            // Céu e Terra
            const skyGrad = ctx.createLinearGradient(0, -100, 0, 100);
            skyGrad.addColorStop(0, '#001a33'); skyGrad.addColorStop(1, '#0059b3');
            const earthGrad = ctx.createLinearGradient(0, -100, 0, 100);
            earthGrad.addColorStop(0, '#331a00'); earthGrad.addColorStop(1, '#1a0d00');
            ctx.fillStyle = skyGrad; ctx.fillRect(-aiW, -400, aiW * 2, 400);
            ctx.fillStyle = earthGrad; ctx.fillRect(-aiW, 0, aiW * 2, 400);

            // Pitch Ladder (Marcadores de grau)
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)'; ctx.lineWidth = 2;
            ctx.font = 'bold 10px Arial'; ctx.fillStyle = 'white'; ctx.textAlign = 'left';
            for (let i = -10; i <= 10; i++) {
                if (i === 0) continue;
                const py = i * 25;
                const pLen = i % 2 === 0 ? 40 : 20;
                ctx.beginPath(); ctx.moveTo(-pLen / 2, py); ctx.lineTo(pLen / 2, py); ctx.stroke();
                if (i % 2 === 0) {
                    ctx.fillText(Math.abs(i * 10).toString(), (pLen / 2) + 5, py + 4);
                }
            }

            // Linha do horizonte neon
            ctx.strokeStyle = '#00f5ff'; ctx.lineWidth = 3; ctx.shadowBlur = 10; ctx.shadowColor = '#00f5ff';
            ctx.beginPath(); ctx.moveTo(-aiW / 2, 0); ctx.lineTo(aiW / 2, 0); ctx.stroke();
            ctx.shadowBlur = 0;
            ctx.restore();

            // Símbolo fixo do avião (Wing Reference)
            ctx.save();
            ctx.translate(aiX + aiW / 2, aiY + aiH / 2);
            ctx.strokeStyle = '#ffcc00'; ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.moveTo(-40, 0); ctx.lineTo(-15, 0); ctx.lineTo(-15, 10); // Asa esquerda
            ctx.moveTo(40, 0); ctx.lineTo(15, 0); ctx.lineTo(15, 10); // Asa direita
            ctx.moveTo(-5, 0); ctx.lineTo(5, 0); // Nariz
            ctx.stroke();
            ctx.restore();

            if (!isMobile) {
                // VSI Aprimorado (Com setas) - Apenas Desktop
                const vsiVal = Math.round(e.vsi);
                const vcolor = vsiVal >= 0 ? '#00ffa3' : '#ff3d00';
                const vsiX = aiX + aiW + (isMobile ? 10 : 25);
                const vsiY = hy + hudH / 2 - 20;
                ctx.save();
                ctx.fillStyle = 'rgba(0,0,0,0.8)'; ctx.strokeStyle = vcolor; ctx.lineWidth = 2;
                ctx.shadowBlur = 8; ctx.shadowColor = vcolor;
                ctx.beginPath(); ctx.roundRect(vsiX, vsiY, isMobile ? 40 : 60, 40, 8); ctx.fill(); ctx.stroke();
                ctx.shadowBlur = 0;
                ctx.fillStyle = vcolor; ctx.font = '900 16px Courier'; ctx.textAlign = 'center';
                ctx.fillText(Math.abs(vsiVal).toString(), vsiX + (isMobile ? 20 : 30), vsiY + 22);
                ctx.font = 'bold 9px Arial'; ctx.fillText('VSI', vsiX + (isMobile ? 20 : 30), vsiY + 34);
                // Seta de tendência VSI
                if (Math.abs(vsiVal) > 50) {
                    ctx.beginPath();
                    if (vsiVal > 0) { ctx.moveTo(vsiX - 10, vsiY + 25); ctx.lineTo(vsiX - 5, vsiY + 15); ctx.lineTo(vsiX, vsiY + 25); }
                    else { ctx.moveTo(vsiX - 10, vsiY + 15); ctx.lineTo(vsiX - 5, vsiY + 25); ctx.lineTo(vsiX, vsiY + 15); }
                    ctx.fill();
                }
                ctx.restore();

                // Heading Indicator (Bússola simulada) - Apenas Desktop
                const hdgW = isMobile ? 65 : 100;
                const hdgX = aiX - (isMobile ? 10 : 25) - hdgW;
                const hdgY = hy + hudH / 2 - 20;
                ctx.save();
                ctx.fillStyle = 'rgba(0,0,0,0.8)'; ctx.strokeStyle = '#00f5ff'; ctx.lineWidth = 2;
                ctx.shadowBlur = 8; ctx.shadowColor = '#00f5ff';
                ctx.beginPath(); ctx.roundRect(hdgX, hdgY, hdgW, 40, 8); ctx.fill(); ctx.stroke();
                ctx.shadowBlur = 0;

                const hdg = Math.round((e.posX / 50) % 360);
                const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
                const dirStr = dirs[Math.floor(((hdg + 22.5) % 360) / 45)];

                ctx.fillStyle = '#00f5ff'; ctx.font = isMobile ? '900 12px Courier' : '900 14px Courier'; ctx.textAlign = 'center';
                ctx.fillText(`${hdg.toString().padStart(3, '0')}° ${dirStr}`, hdgX + hdgW / 2, hdgY + 22);
                ctx.font = 'bold 9px Arial'; ctx.fillText('HDG', hdgX + hdgW / 2, hdgY + 34);
                ctx.restore();
            }

            const tapeW = isMobile ? 60 : 130;
            const currentAlt = Math.round((groundY - e.posY) * (30000 / (groundY - 80)));
            const altTrend = (groundY - e.posY) - e.prevAlt;
            const speedTrend = e.velX - e.prevVelX;

            const tapeOffset = isMobile ? 10 : 20;
            drawModernTape(ctx, hx + tapeW / 2 + tapeOffset, hy + hudH / 2, Math.round(e.velX), isMobile ? 'KT' : 'KNOTS', '#00f5ff', isMobile, speedTrend, true, hudH);
            drawModernTape(ctx, hx + hudW - tapeW / 2 - tapeOffset, hy + hudH / 2, currentAlt, isMobile ? 'FT' : 'FEET', '#00ffcc', isMobile, altTrend, false, hudH);

            const year = Math.floor(startYear + (e.posX / PIXELS_PER_YEAR));
            ctx.textAlign = 'center'; ctx.fillStyle = '#00f5ff'; ctx.font = isMobile ? '900 28px Courier' : '900 44px Courier';
            ctx.shadowBlur = 15; ctx.shadowColor = '#00f5ff';
            ctx.fillText(year, hx + hudW / 2, hy + hudH - (isMobile ? 22 : 30));
            ctx.shadowBlur = 0; ctx.font = 'bold 10px Inter'; ctx.fillStyle = 'rgba(0, 245, 255, 0.6)';
            ctx.fillText('MISSION YEAR', hx + hudW / 2, hy + hudH - (isMobile ? 8 : 12));

            // Mission Progress Mini-Bar inside HUD - CALCULATED REALTIME
            const landingBuffer = 5000;
            const totalDistForPB = (PRESENT_YEAR - startYear) * PIXELS_PER_YEAR + landingBuffer;
            const realTimeProgress = Math.min(100, (e.posX / (totalDistForPB - landingBuffer)) * 100);

            const pbW = aiW; const pbX = aiX; const pbY = hy + hudH - 8;
            ctx.fillStyle = 'rgba(255,255,255,0.1)'; ctx.fillRect(pbX, pbY, pbW, 4);
            ctx.fillStyle = '#00f5ff'; ctx.fillRect(pbX, pbY, pbW * (realTimeProgress / 100), 4);

            ctx.restore();
        };

        const drawModernTape = (ctx, x, y, val, unit, color, isMobile, trend = 0, isSpeed = true, parentH) => {
            const tw = isMobile ? 65 : 110;
            const th = parentH - (isMobile ? 12 : 20); // Responsive height to fit cockpit
            ctx.save(); ctx.translate(x, y);

            // Fundo
            ctx.fillStyle = '#010508';
            ctx.fillRect(-tw / 2, -th / 2, tw, th);

            // Borda Glow
            ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.shadowBlur = 6; ctx.shadowColor = color;
            ctx.strokeRect(-tw / 2, -th / 2, tw, th);
            ctx.shadowBlur = 0;

            // Safe Range Bands (Side color bar)
            const barW = 4;
            if (isSpeed) {
                // Speed: Red < 60, Green 60-150, Yellow > 150
                ctx.fillStyle = '#ff3d00'; ctx.fillRect(tw / 2 - barW, 20, barW, th / 2 - 20); // Stall zone
                ctx.fillStyle = '#00ffa3'; ctx.fillRect(tw / 2 - barW, -30, barW, 50); // Safe zone
                ctx.fillStyle = '#ffcc00'; ctx.fillRect(tw / 2 - barW, -th / 2 + 5, barW, th / 2 - 35); // Fast zone
            } else {
                // Altitude: Red < 500, Green > 2000
                ctx.fillStyle = '#ff3d00'; ctx.fillRect(-tw / 2, 35, barW, th / 2 - 35);
                ctx.fillStyle = '#00ffa3'; ctx.fillRect(-tw / 2, -th / 2 + 5, barW, th / 2 + 25);
            }

            // Ticks
            ctx.strokeStyle = color; ctx.lineWidth = 1;
            for (let i = - (th / 2 - 10); i <= (th / 2 - 10); i += 10) {
                const isMajor = i % 20 === 0;
                ctx.beginPath();
                ctx.moveTo(isMajor ? tw / 2 - 20 : tw / 2 - 12, i); ctx.lineTo(tw / 2, i);
                ctx.moveTo(isMajor ? -tw / 2 + 20 : -tw / 2 + 12, i); ctx.lineTo(-tw / 2, i);
                ctx.stroke();
            }

            // Trend Arrow
            if (Math.abs(trend) > 0.1) {
                ctx.fillStyle = trend > 0 ? '#00ffa3' : '#ff3d00';
                ctx.beginPath();
                const tx = isSpeed ? -tw / 2 + 10 : tw / 2 - 10;
                const ty = -10;
                if (trend > 0) { ctx.moveTo(tx - 4, ty + 8); ctx.lineTo(tx, ty); ctx.lineTo(tx + 4, ty + 8); }
                else { ctx.moveTo(tx - 4, ty); ctx.lineTo(tx, ty + 8); ctx.lineTo(tx + 4, ty); }
                ctx.fill();
            }

            // Value Display
            ctx.shadowBlur = 10; ctx.shadowColor = color; ctx.fillStyle = color;
            ctx.font = isMobile ? '900 20px Courier' : '900 34px Courier'; ctx.textAlign = 'center';
            ctx.fillText(val, 0, 8);
            ctx.shadowBlur = 0; ctx.font = isMobile ? '700 9px Inter' : '700 11px Inter';
            ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.fillText(unit, 0, isMobile ? 30 : 44);
            ctx.restore();
        };

        reqId = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(reqId);
    }, [t, milestones, gameState, startYear, isMuted, showVictoryUI]);

    const navigateToAboutMe = () => {
        onClose();
        setTimeout(() => {
            const el = document.getElementById('about-me');
            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
            else { window.scrollTo({ top: 0, behavior: 'smooth' }); }
        }, 120);
    };

    return createPortal(
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ position: 'fixed', inset: 0, zIndex: 9999999, background: 'rgba(0,0,0,0.96)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div id="game-container" style={{ position: 'relative', width: '95%', height: '90%', background: '#000', borderRadius: '32px', border: '2px solid rgba(0,245,255,0.2)', overflow: 'hidden', boxShadow: '0 0 100px rgba(0,245,255,0.1)' }}>
                <canvas ref={canvasRef} style={{ width: '100%', height: '100%', cursor: 'default' }} />

                {/* GAME PROGRESS BAR - Persistent during milestones */}
                {['TAKEOFF', 'PLAYING', 'FINAL_APPROACH', 'LANDED'].includes(gameState) && (
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '6px', background: 'rgba(255,255,255,0.05)', zIndex: 1000 }}>
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ type: 'spring', stiffness: 45, damping: 15 }}
                            style={{ height: '100%', background: 'linear-gradient(90deg, #00f5ff, #20ff70)', boxShadow: '0 0 15px rgba(0,245,255,0.5)' }}
                        />
                    </div>
                )}

                {/* PERSISTENT HUD BUTTONS */}
                <div style={{ position: 'absolute', top: 25, right: 25, display: 'flex', gap: '15px', zIndex: 1000000 }}>
                    <Button variant="outline-light" onClick={() => setShowInstructions(true)} style={{ borderRadius: '12px', width: '55px', height: '55px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,245,255,0.1)', backdropFilter: 'blur(10px)' }}>
                        <i className="bi bi-info-circle-fill" style={{ fontSize: '1.4rem' }}></i>
                    </Button>
                    <Button variant="outline-light" onClick={() => { initAudio(); setIsMuted(!isMuted); }} style={{ borderRadius: '12px', width: '55px', height: '55px', border: '1px solid rgba(255,255,255,0.2)', background: isMuted ? 'rgba(255,61,0,0.15)' : 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)' }}>
                        <i className={isMuted ? "bi bi-volume-mute-fill" : "bi bi-volume-up-fill"} style={{ fontSize: '1.4rem' }}></i>
                    </Button>
                    <Button variant="outline-light" onClick={onClose} style={{ borderRadius: '12px', width: '55px', height: '55px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)', fontSize: '1.5rem', fontWeight: 'bold' }}>✕</Button>
                </div>

                <AnimatePresence>
                    {showInstructions && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
                            <div style={{ background: 'rgba(5,15,25,0.98)', border: '2px solid #00f5ff', padding: '40px', borderRadius: '32px', maxWidth: '550px', textAlign: 'center' }}>
                                <h2 style={{ color: '#00f5ff', fontSize: '1.6rem', fontWeight: '900', marginBottom: '20px' }}>{t.instructions.title}</h2>
                                <p style={{ fontSize: '0.95rem', color: '#fff', marginBottom: '15px' }}>🖱️ {t.instructions.controls}</p>
                                <p style={{ fontSize: '0.95rem', color: '#fff', marginBottom: '15px' }}>☁️ {t.instructions.rule}</p>
                                <p style={{ fontSize: '0.95rem', color: '#20ff70', fontWeight: 'bold', marginBottom: '30px' }}>🏢 {t.instructions.goal}</p>
                                <Button onClick={() => setShowInstructions(false)} style={{ background: '#00f5ff', color: '#000', border: 'none', borderRadius: '12px', padding: '12px 50px', fontWeight: '900', fontSize: '1.1rem' }}>{t.instructions.btn}</Button>
                            </div>
                        </motion.div>
                    )}

                    {gameState === 'START' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(15px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center', zIndex: 1000 }}>
                            <motion.div initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} transition={{ type: 'spring', damping: 20 }} style={{ border: '2px solid rgba(0,245,255,0.5)', padding: '50px', borderRadius: '40px', background: 'radial-gradient(circle at center, rgba(0,245,255,0.15), transparent)', boxShadow: '0 0 80px rgba(0,245,255,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <h1 style={{ fontWeight: '900', fontSize: '2.5rem', letterSpacing: '8px', color: '#00f5ff', textShadow: '0 0 20px #00f5ff' }}>TIMELINE FLIGHT</h1>
                                <p style={{ opacity: 0.5, fontSize: '0.9rem', marginTop: '10px', letterSpacing: '3px' }}>TRAJECTORY SIMULATION ACTIVATED</p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '40px', width: '100%', alignItems: 'center' }}>
                                    <Button onClick={() => { initAudio(); setGameState('TAKEOFF'); }} style={{ background: '#00f5ff', color: '#000', border: 'none', borderRadius: '12px', padding: '18px 70px', fontWeight: '900', fontSize: '1.5rem', boxShadow: '0 10px 30px rgba(0,245,255,0.2)', width: 'fit-content' }}>{t.begin}</Button>
                                    <Button onClick={() => setShowInstructions(true)} variant="outline-light" style={{ borderRadius: '12px', padding: '12px 30px', fontWeight: '900', fontSize: '0.9rem', width: 'fit-content', border: '1px solid rgba(255,255,255,0.3)' }}>INFO</Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}

                    {gameState === 'LANDED' && (
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.94)', backdropFilter: 'blur(10px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center', padding: '2.5rem', zIndex: 1000 }}>
                            <div style={{ background: 'rgba(2, 8, 15, 0.98)', border: '2px solid rgba(0,245,255,0.4)', padding: '40px', borderRadius: '32px', maxWidth: '650px', boxShadow: '0 0 80px rgba(0,0,0,1)' }}>
                                <h2 style={{ color: '#00f5ff', fontSize: '1.6rem', fontWeight: '900', marginBottom: '20px' }}>{t.landed}</h2>
                                <h3 style={{ fontSize: '1.4rem', margin: '15px 0', color: '#ffffff' }}>{activeMilestone?.title}</h3>

                                {/* Institution and Period Information */}
                                <div style={{ marginTop: '20px', marginBottom: '20px', padding: '20px', background: 'rgba(0,245,255,0.05)', borderRadius: '16px', border: '1px solid rgba(0,245,255,0.2)' }}>
                                    {activeMilestone?.role && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <span style={{ fontSize: '0.85rem', color: 'rgba(0,245,255,0.7)', fontWeight: '700', letterSpacing: '1px' }}>{t.ui?.role || 'FUNÇÃO:'}</span>
                                            <p style={{ fontSize: '1rem', color: '#ffffff', margin: '5px 0 0 0', fontWeight: '600' }}>{activeMilestone.role}</p>
                                        </div>
                                    )}
                                    {activeMilestone?.inst && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <span style={{ fontSize: '0.85rem', color: 'rgba(0,245,255,0.7)', fontWeight: '700', letterSpacing: '1px' }}>{t.ui?.inst || 'INSTITUIÇÃO:'}</span>
                                            <p style={{ fontSize: '1rem', color: '#ffffff', margin: '5px 0 0 0', fontWeight: '600' }}>{activeMilestone.inst}</p>
                                        </div>
                                    )}
                                    {(activeMilestone?.start || activeMilestone?.end) && (
                                        <div>
                                            <span style={{ fontSize: '0.85rem', color: 'rgba(0,245,255,0.7)', fontWeight: '700', letterSpacing: '1px' }}>PERÍODO:</span>
                                            <p style={{ fontSize: '1rem', color: '#ffffff', margin: '5px 0 0 0', fontWeight: '600' }}>
                                                {activeMilestone.start && activeMilestone.end
                                                    ? `${activeMilestone.start} - ${activeMilestone.end}`
                                                    : activeMilestone.start || activeMilestone.end}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', opacity: 0.85, color: 'rgba(255,255,255,0.8)' }}>{activeMilestone?.desc}</p>
                                <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginTop: '40px' }}>
                                    <Button onClick={() => setGameState('PLAYING')} style={{ background: '#00f5ff', color: '#000', border: 'none', borderRadius: '10px', padding: '14px 50px', fontWeight: '900', fontSize: '1.1rem' }}>{t.continue}</Button>
                                    <Button onClick={() => setGameState('START')} variant="outline-light" style={{ borderRadius: '10px', padding: '14px 30px', fontSize: '1rem' }}>RETRY</Button>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {gameState === 'WIN' && showVictoryUI && (
                        <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(20px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center', zIndex: 1000 }}>
                            <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} style={{ background: 'rgba(0,255,163,0.05)', padding: '50px', borderRadius: '48px', border: '3px solid #00ffa3', maxWidth: '850px', boxShadow: '0 0 100px rgba(0,255,163,0.1)' }}>
                                <h1 style={{ color: '#00ffa3', fontSize: '2.2rem', fontWeight: '900', textShadow: '0 0 30px rgba(0,255,163,0.4)', letterSpacing: '2px' }}>{t.victoryTitle}</h1>
                                <p style={{ fontSize: '1.3rem', margin: '20px 0', fontWeight: '500' }}>{t.victorySub}</p>
                                <div style={{ display: 'flex', gap: '25px', justifyContent: 'center', marginTop: '40px' }}>
                                    <Button onClick={navigateToAboutMe} style={{ background: '#00ffa3', color: '#000', border: 'none', borderRadius: '12px', padding: '20px 50px', fontWeight: '900', fontSize: '1.1rem', boxShadow: '0 8px 25px rgba(0,255,163,0.2)' }}>{t.presentationBtn}</Button>
                                    <Button onClick={() => { setShowVictoryUI(false); setGameState('START'); }} variant="outline-light" style={{ border: '2px solid rgba(255,255,255,0.4)', borderRadius: '12px', padding: '20px 40px', fontWeight: '900', fontSize: '1rem' }}>{t.flyAgain}</Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>,
        document.body
    );
}
